import {
  WhmBox,
  WhmSkeleton,
  whmTheme,
  WhmTypography,
  WhmStack,
  SxProps,
  WhmLink,
} from "@securitize/reactjs-whm";
import { ReactComponent as UsdIcon } from "../../../assets/portfolio/usd.svg";
import { ReactComponent as EuroIcon } from "../../../assets/portfolio/euro.svg";
import translationKeys from "../../../contexts/I18nContext/translationKeys";
import { Translate } from "../../Translation/Translate";
import { ReactNode, useMemo } from "react";
import { currencyFormatter } from "../../../helpers/portfolio";
import StyledPortfolioCard from "./StyledCard";
import {
  Currencies,
  AccountStatuses,
  Currency,
} from "../../../data/dtos/cash-account.dto";
import { usePortfolioTopSectionLoadingContext } from "./PortfolioTopSectionContainer";
import useUserInfo from "../../../hooks/useUserInfo";
import { SID } from "@securitize/domain";

const currencyIcons: { [key in Currency]?: ReactNode } = {
  [Currencies.USD]: (
    <WhmBox
      component={UsdIcon}
      sx={{
        width: "32px",
        height: "32px",
      }}
    />
  ),
  [Currencies.EUR]: (
    <WhmBox
      component={EuroIcon}
      sx={{
        width: "32px",
        height: "32px",
      }}
    />
  ),
};

const CASH_ACCOUNT_URL = "/account";

export default function CashBalanceCard({ sx }: { sx?: SxProps }) {
  const { investorType } = useUserInfo();
  const { cashAccountQuery, isContainerLoading: isLoading } =
    usePortfolioTopSectionLoadingContext();
  const isEntity = investorType === SID.InvestorTypes.ENTITY;

  const activeBalances = useMemo(() => {
    return (
      cashAccountQuery.data?.cashBalances.filter(
        (balance) => balance.status === AccountStatuses.CREATED,
      ) || []
    );
  }, [cashAccountQuery.data]);

  const currencyFormat = useMemo(
    () =>
      currencyFormatter({
        currency: Currencies.USD,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [],
  );

  if (isLoading) {
    return (
      <StyledPortfolioCard sx={sx}>
        <WhmStack
          id="cash-balance-skeleton"
          data-test-id="cash-balance-skeleton"
          direction="row"
          spacing={3}
          sx={{ height: "100%" }}
        >
          <WhmStack spacing={4} sx={{ width: "100%" }}>
            <WhmSkeleton
              variant="rounded"
              width={"auto"}
              height={32}
              sx={{ flex: "1", minHeight: "32px" }}
            />
            <WhmSkeleton
              variant="rounded"
              width={"auto"}
              height={32}
              sx={{ flex: "1", minHeight: "32px" }}
            />
          </WhmStack>
          <WhmStack spacing={4} sx={{ width: "100%" }}>
            <WhmSkeleton
              variant="rounded"
              width={"auto"}
              height={32}
              sx={{ flex: "1", minHeight: "32px" }}
            />
            <WhmSkeleton
              variant="rounded"
              width={"auto"}
              height={32}
              sx={{ flex: "1", minHeight: "32px" }}
            />
          </WhmStack>
        </WhmStack>
      </StyledPortfolioCard>
    );
  }

  return (
    <StyledPortfolioCard
      sx={{
        ...sx,
        "& > div": {
          height: "100%",
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
        },
      }}
    >
      <>
        <WhmStack
          direction="row"
          spacing={4}
          justifyContent="space-between"
          alignItems="center"
          marginBottom={4}
        >
          <WhmTypography variant="h4">
            <Translate
              translationKey={translationKeys.CASH_BALANCE_CARD_TITLE}
            />
          </WhmTypography>
          {!cashAccountQuery.data?.shouldShowCashAccountProcessing && (
            <WhmLink href={CASH_ACCOUNT_URL} color="primary" underline="hover">
              <WhmBox sx={{ display: "flex", alignItems: "center" }}>
                <Translate
                  translationKey={translationKeys.CASH_BALANCE_CARD_CTA}
                />
              </WhmBox>
            </WhmLink>
          )}
        </WhmStack>

        {cashAccountQuery.data?.shouldShowCashAccountProcessing ? (
          <>
            <WhmTypography
              sx={{
                color: whmTheme.palette.text.secondary,
                paddingTop: "12px",
              }}
              variant="body1"
            >
              <Translate
                translationKey={translationKeys.CASH_BALANCE_CARD_DESCRIPTION}
              />
            </WhmTypography>
            <WhmTypography
              sx={{
                color: whmTheme.palette.warning.main,
                paddingTop: "12px",
              }}
              variant="body1"
            >
              <Translate
                translationKey={
                  isEntity
                    ? translationKeys.CASH_BALANCE_CARD_PROCESSING_ENTITY
                    : translationKeys.CASH_BALANCE_CARD_PROCESSING
                }
              />
            </WhmTypography>
          </>
        ) : (
          <WhmStack direction="column" spacing={4}>
            {activeBalances.map((balance) => {
              const Icon = currencyIcons[balance.currency];
              return (
                <WhmStack
                  key={balance.currency}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  justifyContent="space-between"
                >
                  <WhmStack direction="row" alignItems="center" spacing={2}>
                    {Icon}
                    <WhmTypography
                      variant="h4"
                      sx={{
                        color: whmTheme.palette.text.primary,
                      }}
                    >
                      {balance.currency}
                    </WhmTypography>
                  </WhmStack>

                  <WhmBox>
                    <WhmTypography variant="h4">
                      {currencyFormat.format(balance.available)}
                    </WhmTypography>
                  </WhmBox>
                </WhmStack>
              );
            })}
          </WhmStack>
        )}
      </>
    </StyledPortfolioCard>
  );
}
